import type { ExportPeriod } from '@/modules/exports/components/export-form'
import {
  ExportCard,
  ExportPeriodField,
  getExportPeriodIso,
} from '@/modules/exports/components/export-form'
import { EXPORT_SALARY_CORRECTIONS } from '@/modules/exports/mutations'
import { useOnErrorAuto } from '@/utils/hooks'
import { isInvalidDate } from '@/utils/time'
import { useMutation } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'

/**
 * The corrections panel on the work period overview shows one driver at a time;
 * this exports the same two lists -- manual salary corrections and rejected
 * activities -- for every driver in the company.
 */
export const ExportSalaryCorrectionsCard: React.FC = () => {
  const translations = useTranslate({
    header: 'settings.corrections-export',
    exportStarted: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
    error: 'error.generic-server-error',
  })

  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const [doExport, { loading }] = useMutation(EXPORT_SALARY_CORRECTIONS, {
    onCompleted(data) {
      if (data.exportSalaryCorrections?.ok) {
        addToast('success', translations.exportStarted)
      } else if (data.exportSalaryCorrections?.exportTimeout) {
        addToast('info', translations.exportTimeout)
      } else {
        addToast('error', translations.error)
      }
    },
    onError: onErrorAuto(translations.error),
  })

  const { formValues: form, updateForm } = useForm<ExportPeriod>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      useDateRange: false,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'Export-salary-corrections-form',
        transformValues: {
          month: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateStart: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateEnd: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          useDateRange: value => value as boolean,
        },
      },
    },
  })

  function handleSubmit() {
    const [dateStart, dateEnd] = getExportPeriodIso(form)

    void doExport({ variables: { dateStart, dateEnd } })
  }

  return (
    <ExportCard
      title={translations.header}
      loading={loading}
      onSubmit={handleSubmit}
    >
      <ExportPeriodField value={form} wide onChange={updateForm} />
    </ExportCard>
  )
}
